import React, { useState } from 'react';
import shared from './GarageContent.module.scss';
import classes from './BillingPanel.module.scss';

// The API's phone rule (POST /auth/update).
const PHONE_PATTERN = /^\+?[1-9]\d{1,14}$/;

// Digits with an optional leading +, as the API stores it: spaces, dashes,
// dots and brackets go, and a 10-digit US number gets its country code.
const normalizePhone = (value) => {
    const stripped = String(value || '').trim().replace(/[\s\-().]/g, '');
    return /^\d{10}$/.test(stripped) ? `1${stripped}` : stripped;
};

// "17032499922" / "+17032499922" -> "703-249-9922"; anything else as stored.
const displayPhone = (phone) => {
    const us = /^\+?1(\d{3})(\d{3})(\d{4})$/.exec(phone || '');
    return us ? `${us[1]}-${us[2]}-${us[3]}` : (phone || '');
};

/**
 * A profile address as display lines: street, unit, "City, ST 12345", and the
 * country only when it is not the US.
 */
export const addressLines = (address) => {
    const a = address || {};
    const cityLine = [a.city, [a.state, a.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    const country = a.country && a.country !== 'US' ? a.country : '';
    return [a.line1, a.line2, cityLine, country].filter(Boolean);
};

const formFrom = (profile) => {
    const a = profile.address || {};
    return {
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        phone: displayPhone(profile.phone),
        line1: a.line1 || '',
        line2: a.line2 || '',
        city: a.city || '',
        state: a.state || '',
        postalCode: a.postalCode || '',
        country: a.country || 'US',
    };
};

// One labelled input of the edit form.
const Field = ({ id, label, wide, ...input }) => (
    <label htmlFor={id} className={`${classes.detailsField} ${wide ? classes.detailsWide : ''}`}>
        {label}
        <input id={id} type="text" {...input} />
    </label>
);

/**
 * "Your details" on the Billing tab: name, phone and address, which our
 * invoices and paperwork are made out to. View mode with an Edit button; the
 * form opens in place. Nothing in the garage waits on it.
 *
 * `onSave(payload)` resolves to { ok, message } (a 409 is the phone number
 * already being on another account, with the server's message).
 */
const YourDetailsCard = ({ profile, onSave }) => {
    const [form, setForm] = useState(null); // null = view mode
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    if (!profile) return null;

    const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ');
    const lines = addressLines(profile.address);
    const missing = !profile.phone || !(profile.address?.line1 && profile.address?.city);

    const edit = () => {
        setForm(formFrom(profile));
        setError('');
    };

    const cancel = () => {
        setForm(null);
        setError('');
    };

    const update = (key) => (e) => setForm((current) => ({ ...current, [key]: e.target.value }));

    const save = async (e) => {
        e.preventDefault();
        if (saving) return;
        const value = (key) => form[key].trim();
        const phone = normalizePhone(form.phone);
        const country = value('country').toUpperCase();
        if (phone && !PHONE_PATTERN.test(phone)) {
            setError('Please check your phone number, for example 703-249-9922.');
            return;
        }
        if (country && !/^[A-Z]{2}$/.test(country)) {
            setError('Please use the two-letter country code, like US.');
            return;
        }
        // The API turns away empty names, and phone numbers are unique per
        // account (a blank one could clash too), so those go only when filled.
        const payload = {
            address: {
                line1: value('line1'),
                line2: value('line2'),
                city: value('city'),
                state: value('state'),
                postalCode: value('postalCode'),
                country: country || 'US',
            },
        };
        if (value('firstName')) payload.firstName = value('firstName');
        if (value('lastName')) payload.lastName = value('lastName');
        if (phone) payload.phone = phone;

        setSaving(true);
        setError('');
        const result = await onSave(payload);
        setSaving(false);
        if (result.ok) setForm(null);
        else setError(result.message);
    };

    const idFor = (key) => `garage-details-${key}`;

    return (
        <section className={`${shared.card} ${classes.details}`} aria-labelledby="garage-details-title">
            <div className={classes.detailsHead}>
                <h2 id="garage-details-title" className={classes.panelTitle}>Your details</h2>
                {!form && (
                    <button type="button" className={shared.actionButton} onClick={edit} aria-label="Edit your details">
                        <i className="fas fa-pen" aria-hidden="true"></i>
                        Edit
                    </button>
                )}
            </div>

            {form ? (
                <form onSubmit={save} noValidate>
                    <div className={classes.detailsForm}>
                        <Field id={idFor('first')} label="First name" value={form.firstName} onChange={update('firstName')} autoComplete="given-name" maxLength={100} disabled={saving} />
                        <Field id={idFor('last')} label="Last name" value={form.lastName} onChange={update('lastName')} autoComplete="family-name" maxLength={100} disabled={saving} />
                        <Field id={idFor('phone')} label="Phone" wide type="tel" value={form.phone} onChange={update('phone')} autoComplete="tel" inputMode="tel" maxLength={24} disabled={saving} />
                        <Field id={idFor('line1')} label="Address" wide value={form.line1} onChange={update('line1')} autoComplete="address-line1" maxLength={200} disabled={saving} />
                        <Field id={idFor('line2')} label="Apt, suite, unit (optional)" wide value={form.line2} onChange={update('line2')} autoComplete="address-line2" maxLength={200} disabled={saving} />
                        <Field id={idFor('city')} label="City" value={form.city} onChange={update('city')} autoComplete="address-level2" maxLength={120} disabled={saving} />
                        <Field id={idFor('state')} label="State" value={form.state} onChange={update('state')} autoComplete="address-level1" maxLength={120} disabled={saving} />
                        <Field id={idFor('zip')} label="ZIP code" value={form.postalCode} onChange={update('postalCode')} autoComplete="postal-code" maxLength={20} disabled={saving} />
                        <Field id={idFor('country')} label="Country" value={form.country} onChange={update('country')} autoComplete="country" maxLength={2} placeholder="US" className={classes.upper} disabled={saving} />
                    </div>
                    <div className={classes.actions}>
                        <button type="submit" className={`${shared.actionButton} ${shared.actionPrimary}`} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" className={shared.actionButton} onClick={cancel} disabled={saving}>
                            Cancel
                        </button>
                    </div>
                    {error && <p className={shared.inlineError} role="alert">{error}</p>}
                </form>
            ) : (
                <>
                    <dl className={`${shared.meta} ${classes.detailsFacts}`}>
                        <div>
                            <dt>Name</dt>
                            <dd>{name || <span className={classes.notSet}>Not added yet</span>}</dd>
                        </div>
                        <div>
                            <dt>Phone</dt>
                            <dd>{profile.phone ? displayPhone(profile.phone) : <span className={classes.notSet}>Not added yet</span>}</dd>
                        </div>
                        <div className={classes.detailsWide}>
                            <dt>Address</dt>
                            <dd>
                                {lines.length
                                    ? lines.map((line, i) => <span key={`${i}-${line}`} className={classes.addressLine}>{line}</span>)
                                    : <span className={classes.notSet}>Not added yet</span>}
                            </dd>
                        </div>
                    </dl>
                    {missing && (
                        <p className={`${classes.payHint} ${classes.detailsHint}`}>
                            <i className="fas fa-info-circle" aria-hidden="true"></i>
                            Add your address and phone so your paperwork is ready when you need it.
                        </p>
                    )}
                </>
            )}
        </section>
    );
};

export default YourDetailsCard;
